import { env } from "../../env";
import Turnstile from "./Turnstile";
import { Button, Card, CardContent, Grid, TextField } from "@mui/material";
import { Formik, FormikHelpers, FormikValues } from "formik";
import React from "react";
import * as Yup from "yup";

interface SignUpFormProps<T> {
  initialValues: FormikValues & T;
  onSubmit: (data: T, helpers: FormikHelpers<FormikValues & T>) => void;
  loading: boolean;
}

function SignUpForm<T>({
  initialValues,
  onSubmit,
  loading,
}: SignUpFormProps<T>) {
  const turnstileEnabled = Boolean(env.REACT_APP_TURNSTILE_SITE_KEY);
  const validationSchema = Yup.object().shape({
    accountName: Yup.string().max(150).required("Account name is required"),
    firstName: Yup.string().max(150).required("First name is required"),
    lastName: Yup.string().max(150).required("Last name is required"),
    email: Yup.string().max(150).required("Email is required"),
    password1: Yup.string().max(150).required("Password is required"),
    password2: Yup.string()
      .max(150)
      .required("Password confirmation is required")
      .test("passwords-match", "Passwords must match", function (value) {
        return this.parent.password1 === value;
      }),
    turnstileToken: turnstileEnabled
      ? Yup.string().required("Captcha verification is required")
      : Yup.string(),
  });

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({
        handleSubmit,
        handleChange,
        isSubmitting,
        setFieldValue,
        values,
        errors,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Grid container spacing={6}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="text"
                      label="Business / Organization Name"
                      variant="outlined"
                      fullWidth
                      name="accountName"
                      value={values.accountName}
                      onChange={handleChange}
                      error={!!errors.accountName}
                      helperText={errors.accountName as string}
                      role="signup-account-name"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="First name"
                      variant="outlined"
                      fullWidth
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      error={!!errors.firstName}
                      helperText={errors.firstName as string}
                      role="signup-firstname"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Last name"
                      variant="outlined"
                      fullWidth
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      error={!!errors.lastName}
                      helperText={errors.lastName as string}
                      role="signup-lastname"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="email"
                      label="Email"
                      variant="outlined"
                      fullWidth
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email as string}
                      role="signup-email"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="password"
                      label="Password"
                      variant="outlined"
                      fullWidth
                      name="password1"
                      value={values.password1}
                      onChange={handleChange}
                      error={!!errors.password1}
                      helperText={errors.password1 as string}
                      role="signup-password1"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      type="password"
                      label="Confirm password"
                      variant="outlined"
                      fullWidth
                      name="password2"
                      value={values.password2}
                      onChange={handleChange}
                      error={!!errors.password2}
                      helperText={errors.password2 as string}
                      role="signup-password2"
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Turnstile
                      error={errors.turnstileToken as string}
                      onTokenChange={(token) =>
                        setFieldValue("turnstileToken", token)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading || isSubmitting}
                      fullWidth
                      role="signup-button"
                    >
                      Sign Up
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </form>
        );
      }}
    </Formik>
  );
}

export default SignUpForm;
